const DEFAULT_PAGE_SIZE = 100
const MAX_PAGES = 30

function repoIdKey(id) {
  return id != null ? String(id) : ''
}

/**
 * Fills repository_slug on contributions that only have repository_id, using as few
 * repository list requests as possible (paginates only until all pending ids are resolved or list ends).
 *
 * @param {Array<Record<string, unknown>>} contributions
 * @param {(params: Record<string, unknown>) => Promise<{ repositories?: Array<{ id?: string, slug?: string }>, total_pages?: number, total?: number }>} listRepos - e.g. repositoryAPI.list
 * @returns {Promise<{ contributions: Array<Record<string, unknown>>, slugByRepoId: Record<string, string> }>}
 */
export async function enrichContributionsWithRepositorySlugs(contributions, listRepos) {
  const list = Array.isArray(contributions) ? [...contributions] : []
  const pendingIds = new Set()
  for (const c of list) {
    if (c.repository_id && !c.repository_slug) {
      pendingIds.add(repoIdKey(c.repository_id))
    }
  }
  if (pendingIds.size === 0) {
    return { contributions: list, slugByRepoId: {} }
  }

  const slugByRepoId = {}
  let page = 1

  while (pendingIds.size > 0 && page <= MAX_PAGES) {
    const response = await listRepos({
      page,
      page_size: DEFAULT_PAGE_SIZE,
      sort: 'updated',
    })
    const repos = response?.repositories || []
    for (const r of repos) {
      const idKey = repoIdKey(r?.id)
      if (idKey && r?.slug && pendingIds.has(idKey)) {
        slugByRepoId[idKey] = r.slug
        pendingIds.delete(idKey)
      }
    }

    const totalPages = Math.max(1, Number(response?.total_pages) || 1)
    if (page >= totalPages || repos.length === 0) break
    page += 1
  }

  const enriched = list.map((c) => {
    const key = repoIdKey(c.repository_id)
    if (c.repository_id && !c.repository_slug && slugByRepoId[key]) {
      return { ...c, repository_slug: slugByRepoId[key] }
    }
    return c
  })

  return { contributions: enriched, slugByRepoId }
}
